import { Box, CircularProgress, Typography, useTheme } from '@mui/material';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { Icon } from '@phosphor-icons/react';
import {
  FileCsv,
  FileHtml,
  Folder,
  ImagesSquare,
  Playlist,
} from '@phosphor-icons/react';
import { getAPIErrorMessage } from '../../../../helpers/helpers';
import { useDarkMode } from '../../../../hooks/useDarkMode';
import { useSidebarData } from '../../../../hooks/useSidebarData';
import { CompanySpacesService } from '../../../../service/CompanySpacesService';
import { UserService } from '../../../../service/UserService';
import { useAuthStore } from '../../../../store/storeAuth';
import {
  NotificationType,
  useNotificationStore,
} from '../../../../store/storeNotifications';
import { useObjectStore } from '../../../../store/storeObjects';
import { Namespace } from '../../../../types/namespace';
import type { VectorEmbeddingModel } from '../../../../types/vector';
import type { GeneratedImage } from '../../../../types/generatedImage';
import type { GeneratedAudio } from '../../../../types/generatedAudio';
import type { GeneratedFile } from '../../../../types/generatedFile';
import { executeVectorMemoryDeletion } from '../executeVectorMemoryDeletion';
import { UserObject } from '../../../../util/Types/User';
import ObjectModal from '../../../Layout/LayoutComponents/FluxObjects/ObjectModal';
import { DeleteNamespaceDialog } from '../../../UI/DeleteNamespaceDialog';
import { VectorMemoryModal } from '../../../UI/VectorMemoryModal';
import { GeneratedImagePreviewModal } from '../../../Layout/LayoutComponents/GeneratedImages/GeneratedImagePreviewModal';
import { GeneratedAudioPreviewModal } from '../../../Layout/LayoutComponents/GeneratedAudio/GeneratedAudioPreviewModal';
import { GeneratedFilePreviewModal } from '../../../Layout/LayoutComponents/GeneratedFile/GeneratedFilePreviewModal';
import { FluxObject } from '../AgentsSidebar/context';
import { useExtendedSidebarContext } from '../ExtendedSidebar';
import { CollapsibleFolderGroup } from './CollapsibleFolderGroup';
import { GeneratedAudioItem } from './GeneratedAudioItem';
import { GeneratedFileItem } from './GeneratedFileItem';
import { GeneratedImageItem } from './GeneratedImageItem';
import { ObjectItem } from './ObjectItem';
import { RenameAssetModal } from './RenameAssetModal';
import { Section } from './Section';
import { VectorMemoryItem } from './VectorMemoryItem';
import {
  DataSidebarContext,
  DataSidebarContextValue,
  useDataSidebarContext,
} from './context';
import { categorizeGeneratedFiles } from './categorizeFiles';
import { useGeneratedAssetsQueries } from './hooks/useGeneratedAssetsQueries';
import { useAssetHandlers } from './hooks/useAssetHandlers';
import { useSpaceActions } from './hooks/useSpaceActions';

interface OrganizedItems {
  objects: FluxObject[];
  vectorMemory: Namespace[];
  generatedImages: GeneratedImage[];
  generatedAudio: GeneratedAudio[];
  generatedFiles: GeneratedFile[];
}

interface FolderConfig {
  title: string;
  icon: Icon;
  images?: GeneratedImage[];
  audios?: GeneratedAudio[];
  files?: GeneratedFile[];
}

function filterBySearch(items: OrganizedItems, term: string): OrganizedItems {
  const lower = term.trim().toLowerCase();
  if (!lower) return items;
  return {
    objects: items.objects.filter(o => o.name?.toLowerCase().includes(lower)),
    vectorMemory: items.vectorMemory.filter(ns =>
      ns.name?.toLowerCase().includes(lower),
    ),
    generatedImages: items.generatedImages.filter(
      img =>
        img.provider?.toLowerCase().includes(lower) ||
        img.model?.toLowerCase().includes(lower),
    ),
    generatedAudio: items.generatedAudio.filter(
      audio =>
        audio.provider?.toLowerCase().includes(lower) ||
        audio.model?.toLowerCase().includes(lower),
    ),
    generatedFiles: items.generatedFiles.filter(
      file =>
        file.name?.toLowerCase().includes(lower) ||
        file.provider?.toLowerCase().includes(lower) ||
        file.fileType?.toLowerCase().includes(lower),
    ),
  };
}

interface ItemsSectionProps {
  items: OrganizedItems;
  onObjectEdit: (obj: FluxObject) => void;
  onObjectDelete: (obj: FluxObject) => void;
  onVectorEdit: (ns: Namespace) => void;
  onVectorDelete: (ns: Namespace) => void;
  onImagePreview: (image: GeneratedImage) => void;
  onImageRename: (image: GeneratedImage) => void;
  onImageDelete: (image: GeneratedImage) => void;
  onAudioPreview: (audio: GeneratedAudio) => void;
  onAudioRename: (audio: GeneratedAudio) => void;
  onAudioDelete: (audio: GeneratedAudio) => void;
  onFilePreview: (file: GeneratedFile) => void;
  onFileRename: (file: GeneratedFile) => void;
  onFileDelete: (file: GeneratedFile) => void;
}

const ItemsSection: React.FC<ItemsSectionProps> = ({
  items,
  onObjectEdit,
  onObjectDelete,
  onVectorEdit,
  onVectorDelete,
  onImagePreview,
  onImageRename,
  onImageDelete,
  onAudioPreview,
  onAudioRename,
  onAudioDelete,
  onFilePreview,
  onFileRename,
  onFileDelete,
}) => {
  const { docs, pdfs, csvFiles, htmlFiles, imageFiles, others } =
    categorizeGeneratedFiles(items.generatedFiles);

  const folders: FolderConfig[] = [
    { title: 'Audios', icon: Playlist, audios: items.generatedAudio },
    { title: 'Docs', icon: Folder, files: docs },
    {
      title: 'Images',
      icon: ImagesSquare,
      images: items.generatedImages,
      files: imageFiles,
    },
    { title: 'PDFs', icon: Folder, files: pdfs },
    { title: 'CSV', icon: FileCsv, files: csvFiles },
    { title: 'HTML', icon: FileHtml, files: htmlFiles },
    { title: 'Others', icon: Folder, files: others },
  ];

  const nonEmptyFolders = folders.filter(
    f =>
      (f.images?.length ?? 0) +
        (f.audios?.length ?? 0) +
        (f.files?.length ?? 0) >
      0,
  );

  return (
    <>
      {items.objects.map(obj => (
        <ObjectItem
          key={obj.id}
          object={obj}
          onEdit={onObjectEdit}
          onDelete={onObjectDelete}
          isNested={false}
        />
      ))}
      {items.vectorMemory.length > 0 && (
        <CollapsibleFolderGroup title='Vector Memory' defaultExpanded>
          {items.vectorMemory.map(ns => (
            <VectorMemoryItem
              key={ns.id}
              namespace={ns}
              onEdit={onVectorEdit}
              onDelete={onVectorDelete}
              isNested
            />
          ))}
        </CollapsibleFolderGroup>
      )}
      {nonEmptyFolders.length > 0 && (
        <CollapsibleFolderGroup
          title='Generated Files'
          icon={Folder}
          defaultExpanded={false}
        >
          {nonEmptyFolders.map(folder => (
            <CollapsibleFolderGroup
              key={folder.title}
              title={folder.title}
              icon={folder.icon}
              defaultExpanded={false}
            >
              {folder.images?.map(image => (
                <GeneratedImageItem
                  key={image.id}
                  image={image}
                  onPreview={onImagePreview}
                  onRename={onImageRename}
                  onDelete={onImageDelete}
                  isNested
                />
              ))}
              {folder.audios?.map(audio => (
                <GeneratedAudioItem
                  key={audio.id}
                  audio={audio}
                  onPreview={onAudioPreview}
                  onRename={onAudioRename}
                  onDelete={onAudioDelete}
                  isNested
                />
              ))}
              {folder.files?.map(file => (
                <GeneratedFileItem
                  key={file.id}
                  file={file}
                  onPreview={onFilePreview}
                  onRename={onFileRename}
                  onDelete={onFileDelete}
                  isNested
                />
              ))}
            </CollapsibleFolderGroup>
          ))}
        </CollapsibleFolderGroup>
      )}
    </>
  );
};

export const DataSidebarBody: React.FC = () => {
  const theme = useTheme();
  const { customColorPalette: palette } = theme;
  const isDarkMode = useDarkMode();

  const { searchTerm } = useExtendedSidebarContext();
  const { user: authUser, setAuthUser } = useAuthStore(state => state);
  const {
    setObjectUpdate,
    resetObjectUpdate,
    removeObject,
    setObjects,
    objects: objectStore,
  } = useObjectStore(state => state);
  const setNotification = useNotificationStore(state => state.setNotification);
  const {
    spaces,
    user: sidebarUser,
    isLoading,
    refresh,
    updateSpace,
  } = useSidebarData();

  const [activeObjectId, setActiveObjectId] = useState<string | undefined>();
  const [editingObject, setEditingObject] = useState<FluxObject | null>(null);
  const [editingVector, setEditingVector] = useState<Namespace | null>(null);
  const [pendingVectorNamespace, setPendingVectorNamespace] =
    useState<Namespace | null>(null);

  const {
    privateImages,
    privateAudio,
    privateFiles,
    spaceImageResults,
    spaceAudioResults,
    spaceFileResults,
    deleteGeneratedImage,
    deleteGeneratedAudio,
    deleteGeneratedFile,
    queryClient,
  } = useGeneratedAssetsQueries(spaces, refresh);

  const { modals, dispatch, handlers, renameModal } = useAssetHandlers(
    queryClient,
    { deleteGeneratedImage, deleteGeneratedAudio, deleteGeneratedFile },
  );

  const { handleEditSpace, handleArchiveSpace } = useSpaceActions(refresh);

  useEffect(() => {
    if (sidebarUser) setAuthUser(sidebarUser);
  }, [sidebarUser, setAuthUser]);

  const privateItems = useMemo<OrganizedItems>(
    () => ({
      objects: objectStore['user'] || authUser?.objects || [],
      vectorMemory: (authUser?.namespaces as Namespace[]) || [],
      generatedImages: privateImages,
      generatedAudio: privateAudio,
      generatedFiles: privateFiles,
    }),
    [
      objectStore,
      authUser?.objects,
      authUser?.namespaces,
      privateImages,
      privateAudio,
      privateFiles,
    ],
  );

  const spaceItems = useMemo(
    () =>
      spaces.map((space, index) => ({
        space,
        items: {
          objects: objectStore[space.id]?.length
            ? objectStore[space.id]
            : space.objects || [],
          vectorMemory: space.namespaces || [],
          generatedImages: spaceImageResults[index]?.data?.data ?? [],
          generatedAudio: spaceAudioResults[index]?.data?.data ?? [],
          generatedFiles: spaceFileResults[index]?.data?.data ?? [],
        } as OrganizedItems,
      })),
    [
      spaces,
      objectStore,
      spaceImageResults,
      spaceAudioResults,
      spaceFileResults,
    ],
  );

  const sortedSpaces = useMemo(
    () =>
      [...spaceItems].sort(
        (a, b) => Number(!!a.space.disabled) - Number(!!b.space.disabled),
      ),
    [spaceItems],
  );

  const searchResults = useMemo(() => {
    if (!searchTerm.trim()) return null;
    const all: OrganizedItems = {
      objects: [
        privateItems.objects,
        ...spaceItems.map(s => s.items.objects),
      ].flat(),
      vectorMemory: [
        privateItems.vectorMemory,
        ...spaceItems.map(s => s.items.vectorMemory),
      ].flat(),
      generatedImages: [
        privateItems.generatedImages,
        ...spaceItems.map(s => s.items.generatedImages),
      ].flat(),
      generatedAudio: [
        privateItems.generatedAudio,
        ...spaceItems.map(s => s.items.generatedAudio),
      ].flat(),
      generatedFiles: [
        privateItems.generatedFiles,
        ...spaceItems.map(s => s.items.generatedFiles),
      ].flat(),
    };
    return filterBySearch(all, searchTerm);
  }, [searchTerm, privateItems, spaceItems]);

  const handleObjectEdit = useCallback(
    (obj: FluxObject) => {
      const full =
        (authUser?.objects ?? []).find(o => o.id === obj.id) ??
        (spaces.flatMap(s => s.objects ?? []).find(o => o.id === obj.id) as
          | UserObject
          | undefined);
      if (!full) return;
      setObjectUpdate({
        isCreating: false,
        id: full.id,
        name: full.name,
        text: full.text,
        sessions: full.sessions,
        localObject: full.localObject,
        sessionEnabled: full.sessionEnabled,
        flowId: full.flowId,
      });
      setEditingObject(full as FluxObject);
    },
    [authUser?.objects, spaces, setObjectUpdate],
  );

  const handleObjectDelete = useCallback(
    async (obj: FluxObject) => {
      if (!window.confirm('Are you sure you want to delete this object?'))
        return;
      const spaceId = spaces.find(s =>
        s.objects?.some(o => o.id === obj.id),
      )?.id;
      removeObject(obj.id, spaceId ?? 'user');
      try {
        if (spaceId) {
          const space = spaces.find(s => s.id === spaceId);
          const updated = (space?.objects ?? []).filter(o => o.id !== obj.id);
          await CompanySpacesService.updateCompanySpace(
            authUser?.selectedCompanyId!,
            { spaceId, objects: updated },
          );
          setObjects(updated as UserObject[], spaceId);
        } else {
          const updated = (authUser?.objects ?? []).filter(
            o => o.id !== obj.id,
          );
          await UserService.updateUser({ objects: updated });
          setObjects(updated, 'user');
          refresh();
        }
        setNotification({
          type: NotificationType.Success,
          message: 'Object deleted successfully',
        });
      } catch (error) {
        refresh();
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error as any),
        });
      }
    },
    [authUser, spaces, removeObject, setObjects, refresh, setNotification],
  );

  if (isLoading && spaces.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const { imagePreview, audioPreview, filePreview } = modals;

  const assetTypeLabels: Record<string, string> = {
    image: 'Image',
    audio: 'Audio',
    file: 'File',
  };

  const contextValue: DataSidebarContextValue = {
    isDarkMode,
    palette,
    activeObjectId,
    onObjectClick: obj => setActiveObjectId(obj.id),
  };

  const itemHandlers: Omit<ItemsSectionProps, 'items'> = {
    onObjectEdit: handleObjectEdit,
    onObjectDelete: handleObjectDelete,
    onVectorEdit: ns => setEditingVector(ns),
    onVectorDelete: ns => setPendingVectorNamespace(ns),
    ...handlers,
  };

  return (
    <DataSidebarContext.Provider value={contextValue}>
      {editingObject && (
        <ObjectModal
          show
          onHide={async () => {
            setEditingObject(null);
            resetObjectUpdate();
            refresh();
          }}
          spaceId={
            spaces.find(s =>
              s.objects?.some(o => o.id === editingObject.id),
            )?.id ?? 'user'
          }
        />
      )}

      {editingVector && (
        <VectorMemoryModal
          open
          namespaceId={editingVector.id}
          spaceId={
            spaces.find(s =>
              s.namespaces?.some(n => n.id === editingVector.id),
            )?.id
          }
          onOpenChange={async ({ open }) => {
            if (!open) {
              setEditingVector(null);
              refresh();
            }
          }}
        />
      )}

      <GeneratedImagePreviewModal
        image={imagePreview.image}
        open={imagePreview.show}
        onClose={() => dispatch({ type: 'CLOSE_IMAGE_PREVIEW' })}
      />

      <GeneratedAudioPreviewModal
        audio={audioPreview.audio}
        open={audioPreview.show}
        onClose={() => dispatch({ type: 'CLOSE_AUDIO_PREVIEW' })}
      />

      <GeneratedFilePreviewModal
        file={filePreview.file}
        open={filePreview.show}
        onClose={() => dispatch({ type: 'CLOSE_FILE_PREVIEW' })}
      />

      <RenameAssetModal
        show={renameModal.show}
        currentName={renameModal.currentName}
        assetType={assetTypeLabels[renameModal.type ?? ''] ?? 'File'}
        onHide={renameModal.onHide}
        onRename={renameModal.onRename}
      />

      {pendingVectorNamespace ? (
        <DeleteNamespaceDialog
          open
          embeddingModelName={
            (pendingVectorNamespace.embeddingModelName ||
              'voyage-3-large') as VectorEmbeddingModel
          }
          id={pendingVectorNamespace.id}
          name={pendingVectorNamespace.name}
          onOpenChange={open => {
            if (!open) setPendingVectorNamespace(null);
          }}
          onNamespaceDelete={({
            embeddingModelName,
            namespaceId,
            namespaceName,
          }) => {
            void executeVectorMemoryDeletion(
              {
                id: namespaceId,
                name: namespaceName,
                embeddingModelName,
              } as Namespace,
              {
                spaces,
                updateSpace,
                refreshSidebar: refresh,
                setNotification,
              },
            ).then(() => setPendingVectorNamespace(null));
          }}
        />
      ) : null}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {searchResults ? (
          <ItemsSection items={searchResults} {...itemHandlers} />
        ) : (
          <>
            <Section
              title='Private'
              hasBorder={spaces.length > 0}
              isAccordion
              defaultExpanded
            >
              <ItemsSection items={privateItems} {...itemHandlers} />
            </Section>

            {sortedSpaces.map(({ space, items }, index) => {
              const isEmptySpace =
                items.objects.length === 0 &&
                items.vectorMemory.length === 0 &&
                items.generatedImages.length === 0 &&
                items.generatedAudio.length === 0 &&
                items.generatedFiles.length === 0;

              return (
                <Section
                  key={space.id}
                  title={space.name}
                  hasBorder={index < sortedSpaces.length - 1}
                  isAccordion
                  defaultExpanded={false}
                  isDisabled={!!space.disabled}
                  onEditSpace={() => handleEditSpace(space.id)}
                  onArchive={() => handleArchiveSpace(space.id)}
                >
                  {isEmptySpace && (
                    <Typography
                      sx={{
                        color: isDarkMode
                          ? palette.gray[500]
                          : palette.gray[400],
                        fontFamily: 'Roboto',
                        fontSize: '12px',
                        fontStyle: 'italic',
                        padding: '8px',
                      }}
                    >
                      No items found
                    </Typography>
                  )}
                  <ItemsSection items={items} {...itemHandlers} />
                </Section>
              );
            })}
          </>
        )}
      </Box>
    </DataSidebarContext.Provider>
  );
};
